import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Camera, Copy, Pencil, Trash2, FileText } from 'lucide-react';
import { api } from '../api/client';
import type { ActionWithStatus, CaptureLogEntry } from '@databro/shared';
import RunActionModal from '../components/RunActionModal';
import ActionForm from '../components/ActionForm';
import {
  subscribeToCaptureLog,
  subscribeToCaptureFailed,
  subscribeToCaptureCompleted,
  subscribeToCaptureStarted,
} from '../hooks/useWebSocket';
import {
  Card,
  CardContent,
  Button,
  StatusBadge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  Skeleton,
} from '@databro/ui';

export default function Actions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingAction, setEditingAction] = useState<ActionWithStatus | null>(null);
  const [runningAction, setRunningAction] = useState<ActionWithStatus | null>(null);
  const [captureLogsAction, setCaptureLogsAction] = useState<ActionWithStatus | null>(null);
  const [pendingCaptureActionId, setPendingCaptureActionId] = useState<string | null>(null);

  // Subscribe to capture_started to navigate to action detail page
  useEffect(() => {
    const unsub = subscribeToCaptureStarted((actionId, _captureSessionId) => {
      if (pendingCaptureActionId === actionId) {
        setPendingCaptureActionId(null);
        navigate(`/actions/${actionId}`);
      }
    });
    return unsub;
  }, [pendingCaptureActionId, navigate]);

  const { data: actions, isLoading } = useQuery({
    queryKey: ['actions'],
    queryFn: () => api.getActions(),
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const cloneMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.cloneAction(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const clearCredentialsMutation = useMutation({
    mutationFn: (id: string) => api.clearCapturedRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const captureMutation = useMutation({
    mutationFn: (id: string) => {
      setPendingCaptureActionId(id);
      return api.triggerCapture(id);
    },
    onSuccess: () => {
      // Navigation will happen via WebSocket capture_started event
    },
    onError: (err) => {
      setPendingCaptureActionId(null);
      alert(err instanceof Error ? err.message : 'Failed to trigger capture');
    },
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Actions</h1>
          <p className="text-muted-foreground">Manage your data scraping actions</p>
        </div>
        <Button
          onClick={() => {
            setEditingAction(null);
            setShowForm(true);
          }}
        >
          Create Action
        </Button>
      </div>

      {showForm && (
        <ActionForm
          action={editingAction}
          clients={clients || []}
          onClose={() => {
            setShowForm(false);
            setEditingAction(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingAction(null);
            queryClient.invalidateQueries({ queryKey: ['actions'] });
          }}
        />
      )}

      {runningAction && (
        <RunActionModal
          action={runningAction}
          onClose={() => setRunningAction(null)}
          onSuccess={() => setRunningAction(null)}
        />
      )}

      {captureLogsAction && (
        <CaptureLogsModal
          action={captureLogsAction}
          onClose={() => setCaptureLogsAction(null)}
        />
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {actions?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No actions yet. Create your first action to get started.
                </TableCell>
              </TableRow>
            )}
            {actions?.map((action) => (
              <TableRow key={action.id}>
                <TableCell>
                  <Link
                    to={`/actions/${action.id}`}
                    className="font-medium hover:underline"
                  >
                    {action.name}
                  </Link>
                  {!action.hasCredentials && (
                    <Badge variant="warning" className="ml-2">Needs auth</Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{action.platform}</TableCell>
                <TableCell className="text-muted-foreground">
                  {action.clients.length > 0
                    ? action.clients.length === 1
                      ? action.clients[0].name || action.clients[0].id.slice(0, 8)
                      : `${action.clients.length} clients`
                    : '-'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={action.lastExecution?.status || 'idle'} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {action.scheduleType === 'periodic'
                    ? formatInterval(action.intervalMs || 0)
                    : 'Manual'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setRunningAction(action)}
                      disabled={!action.hasCredentials}
                      title="Run"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (action.hasCredentials) {
                          if (confirm('Clear captured credentials and start fresh capture?')) {
                            clearCredentialsMutation.mutate(action.id, {
                              onSuccess: () => captureMutation.mutate(action.id),
                            });
                          }
                        } else {
                          captureMutation.mutate(action.id);
                        }
                      }}
                      disabled={captureMutation.isPending || clearCredentialsMutation.isPending}
                      title={action.hasCredentials ? 'Re-capture' : 'Capture'}
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCaptureLogsAction(action)}
                      disabled={!action.hasCredentials}
                      title="Capture Logs"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newName = prompt('Enter name for duplicated action:', `${action.name} (copy)`);
                        if (newName) {
                          cloneMutation.mutate({ id: action.id, name: newName });
                        }
                      }}
                      disabled={cloneMutation.isPending}
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingAction(action);
                        setShowForm(true);
                      }}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this action?')) {
                          deleteMutation.mutate(action.id);
                        }
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function formatInterval(ms: number): string {
  if (ms < 60000) return `${ms / 1000}s`;
  if (ms < 3600000) return `${ms / 60000}m`;
  return `${ms / 3600000}h`;
}

function CaptureLogsModal({
  action,
  onClose,
}: {
  action: ActionWithStatus;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track real-time logs separately
  const [realtimeLogs, setRealtimeLogs] = useState<CaptureLogEntry[]>([]);
  const [captureStatus, setCaptureStatus] = useState<'idle' | 'capturing' | 'completed' | 'failed'>('idle');
  const [captureError, setCaptureError] = useState<string | null>(null);

  // Fetch historical logs
  const { data, isLoading, error } = useQuery({
    queryKey: ['capture-logs', action.id],
    queryFn: () => api.getCaptureLogs(action.id),
  });

  // Subscribe to real-time capture events
  useEffect(() => {
    const unsubLog = subscribeToCaptureLog((actionId, _captureSessionId, log) => {
      if (actionId === action.id) {
        setCaptureStatus('capturing');
        setRealtimeLogs((prev) => [...prev, log]);
        // Auto-scroll to bottom
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }, 50);
      }
    });

    const unsubFailed = subscribeToCaptureFailed((actionId, _captureSessionId, error) => {
      if (actionId === action.id) {
        setCaptureStatus('failed');
        setCaptureError(error);
        // Don't clear realtime logs on failure - keep them for debugging
      }
    });

    const unsubCompleted = subscribeToCaptureCompleted((actionId, _captureSessionId) => {
      if (actionId === action.id) {
        setCaptureStatus('completed');
        // Refresh the historical logs
        queryClient.invalidateQueries({ queryKey: ['capture-logs', action.id] });
        // Clear realtime logs after refresh
        setTimeout(() => setRealtimeLogs([]), 1000);
      }
    });

    return () => {
      unsubLog();
      unsubFailed();
      unsubCompleted();
    };
  }, [action.id, queryClient]);

  const getStageStyle = (stage: string) => {
    if (stage.includes('completed') || stage.includes('captured') || stage === 'pattern_matched') {
      return { icon: '✓', variant: 'success' as const };
    }
    if (stage.includes('failed') || stage === 'pattern_not_matched') {
      return { icon: '✕', variant: 'destructive' as const };
    }
    if (stage.includes('started') || stage.includes('opened') || stage.includes('set')) {
      return { icon: '●', variant: 'default' as const };
    }
    if (stage.includes('intercepted')) {
      return { icon: '○', variant: 'secondary' as const };
    }
    return { icon: '○', variant: 'secondary' as const };
  };

  // Determine which logs to show
  const isCapturing = captureStatus === 'capturing' || realtimeLogs.length > 0;
  const logsToShow = isCapturing ? realtimeLogs : (data?.logs || []);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Capture Logs - {action.name}
            {captureStatus === 'capturing' && (
              <Badge variant="warning" className="animate-pulse">Live</Badge>
            )}
            {captureStatus === 'completed' && (
              <Badge variant="success">Completed</Badge>
            )}
            {captureStatus === 'failed' && (
              <Badge variant="destructive">Failed</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div ref={scrollRef} className="overflow-y-auto max-h-[calc(90vh-140px)] pr-2">
          {isLoading && !isCapturing && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          )}

          {error && !isCapturing && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-md">
              {error instanceof Error ? error.message : 'Failed to load capture logs'}
            </div>
          )}

          {captureError && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-md mb-4">
              Capture failed: {captureError}
            </div>
          )}

          {(data || isCapturing) && (
            <div className="space-y-4">
              {!isCapturing && data && (
                <div className="text-sm text-muted-foreground">
                  {data.completedAt
                    ? `Captured: ${new Date(data.completedAt).toLocaleString()}`
                    : `Started: ${new Date(data.startedAt).toLocaleString()}`}
                  {data.status && ` (${data.status})`}
                </div>
              )}

              {isCapturing && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  Capture in progress...
                </div>
              )}

              {logsToShow.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {isCapturing
                    ? 'Waiting for capture logs...'
                    : 'No capture logs available. This may be an older capture without logging.'}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-4">
                    <div className="relative">
                      <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-border" />

                      <div className="space-y-3">
                        {logsToShow.map((log: CaptureLogEntry, index: number) => {
                          const { icon, variant } = getStageStyle(log.stage);

                          return (
                            <div key={`${log.timestamp}-${index}`} className="relative flex items-start gap-3">
                              <div
                                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  variant === 'success'
                                    ? 'bg-green-500/15 text-green-500'
                                    : variant === 'destructive'
                                    ? 'bg-destructive/15 text-destructive'
                                    : variant === 'default'
                                    ? 'bg-primary/15 text-primary'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {icon}
                              </div>

                              <div className="flex-grow min-w-0 pt-0.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm">{log.message}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {log.stage.replace(/_/g, ' ')}
                                  </Badge>
                                </div>

                                {log.details && Object.keys(log.details).length > 0 && (
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    {Object.entries(log.details).map(([key, value]) => (
                                      <div key={key} className="truncate max-w-lg">
                                        <span className="font-medium">{key}:</span>{' '}
                                        {typeof value === 'string'
                                          ? value
                                          : JSON.stringify(value)}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {new Date(log.timestamp).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
